import { Box, useMediaQuery } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "../../../../components/data-table/DataTable";
import { DeleteActionCell } from "../../../../components/data-table/DeleteActionCell";
import { RemoveGroupAccessDialog } from "../../../../components/RemoveGroupAccessDialog";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import { Routes } from "../../../../types/NavigationPaths";
import { TExperienceGroupRow } from "../../hooks/useExperienceGroups";
import { GrantAccessToGroupDialog } from "../GrantAccessToGroupDialog";

interface ExperienceGroupsTabProps {
  releaseChannelId: string;
  experienceName: string;
  rows: TExperienceGroupRow[];
  isLoading: boolean;
  isExternal?: boolean;
  licenseId?: string;
  licenseExpiresAt: Date | null;
}

interface GroupNameCellProps {
  row: TExperienceGroupRow;
  onNavigate: (groupId: string) => void;
}

const GroupNameCell: React.FC<GroupNameCellProps> = ({ row, onNavigate }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onNavigate(row.userGroupId);
  };

  return (
    <Box
      component="button"
      onClick={handleClick}
      sx={{
        border: 0,
        p: 0,
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        fontSize: "inherit",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
        display: "block",
      }}
    >
      {row.userGroupName}
    </Box>
  );
};

interface ActionsCellProps {
  row: TExperienceGroupRow;
  onRemove: (row: TExperienceGroupRow) => void;
  actionText: string;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  onRemove,
  row,
  actionText,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(row);
  };

  return <DeleteActionCell actionText={actionText} onClick={handleClick} />;
};

export const ExperienceGroupsTab: React.FC<ExperienceGroupsTabProps> = ({
  releaseChannelId,
  experienceName,
  rows,
  isLoading,
  isExternal = false,
  licenseId,
  licenseExpiresAt,
}) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isCompact = useMediaQuery("(max-width: 1150px)");

  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] =
    useState<TExperienceGroupRow | null>(null);

  const [isGrantAccessDialogOpen, setIsGrantAccessDialogOpen] = useState(false);
  const handleGrantAccessDialogClose = useCallback(() => {
    setIsGrantAccessDialogOpen(false);
  }, []);

  const handleGrantAccessDialogOpen = useCallback(() => {
    setIsGrantAccessDialogOpen(true);
  }, []);

  const { companyId } = useParams();

  const handleNavigateToGroup = useCallback(
    (groupId: string) => {
      void navigate(`/companies/${companyId}${Routes.GROUPS}/${groupId}`);
    },
    [navigate, companyId],
  );

  const handleOpenRemove = useCallback((row: TExperienceGroupRow) => {
    setSelectedGroup(row);
    setRemoveDialogOpen(true);
  }, []);

  const handleCloseRemove = useCallback(() => {
    setRemoveDialogOpen(false);
    setSelectedGroup(null);
  }, []);

  const handleRowClick = useCallback(
    (row: TExperienceGroupRow) => {
      void navigate(`/companies/${companyId}${Routes.GROUPS}/${row.userGroupId}`);
    },
    [navigate, companyId],
  );

  const renderGroupNameCell = useCallback(
    (row: TExperienceGroupRow) => (
      <GroupNameCell onNavigate={handleNavigateToGroup} row={row} />
    ),
    [handleNavigateToGroup],
  );

  const renderActionsCell = useCallback(
    (row: TExperienceGroupRow) => (
      <ActionsCell
        actionText={
          isMobile
            ? t("experiences.groupsTab.remove")
            : t("experiences.groupsTab.removeAccess")
        }
        onRemove={handleOpenRemove}
        row={row}
      />
    ),
    [handleOpenRemove, isMobile, t],
  );

  const columns = useMemo<ColumnDef<TExperienceGroupRow>[]>(
    () => [
      // 1. Name
      {
        accessorKey: "userGroupName",
        id: "name",
        header: t("experiences.groupsTab.nameHeader"),
        size: isCompact ? 140 : 240,
        minSize: 100,
        maxSize: 400,
        cell: ({ row }) => renderGroupNameCell(row.original),
        enableSorting: true,
      },
      // 2. Users
      {
        accessorKey: "userCount",
        id: "users",
        header: t("experiences.groupsTab.usersHeader"),
        size: isCompact ? 100 : 220,
        minSize: 80,
        maxSize: 300,
        cell: ({ row }) => row.original.userCount,
        enableSorting: true,
      },
      // 3. Experiences
      {
        accessorKey: "experienceCount",
        id: "experiences",
        header: t("experiences.groupsTab.experiencesHeader"),
        size: isCompact ? 100 : 220,
        minSize: 80,
        maxSize: 300,
        cell: ({ row }) => row.original.experienceCount,
        enableSorting: true,
      },
      // 4. Actions
      {
        id: "actions",
        header: t("experiences.groupsTab.actionsHeader"),
        size: 160,
        minSize: 160,
        maxSize: 160,
        cell: ({ row }) => renderActionsCell(row.original),
        enableSorting: false,
      },
    ],
    [renderGroupNameCell, renderActionsCell, isCompact, t],
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflowX: "auto",
        }}
      >
        <DataTable
          actionButtonText={t("experiences.groupsTab.grantAccess")}
          actionButtonTextShort={t("experiences.grantAccessToGroup.grant")}
          columns={columns}
          data={rows}
          defaultSort={[{ id: "name", desc: false }]}
          globalFilterPlaceholder={t(
            "experiences.grantAccessToGroup.searchPlaceholder",
          )}
          isLoading={isLoading}
          onActionClick={handleGrantAccessDialogOpen}
          onRowClick={handleRowClick}
        />
      </Box>

      <GrantAccessToGroupDialog
        isExternal={isExternal}
        isOpen={isGrantAccessDialogOpen}
        licenseId={licenseId}
        maxDate={licenseExpiresAt}
        onClose={handleGrantAccessDialogClose}
        releaseChannelId={releaseChannelId}
      />

      <RemoveGroupAccessDialog
        experienceName={experienceName}
        groupName={selectedGroup?.userGroupName ?? ""}
        isExternal={isExternal}
        licenseId={licenseId}
        onClose={handleCloseRemove}
        open={removeDialogOpen}
        releaseChannelId={releaseChannelId}
        userGroupId={selectedGroup?.userGroupId ?? ""}
      />
    </>
  );
};
